// src/components/shared/ClassTimetableCalendar.jsx
import React, { useState, useMemo, useEffect } from 'react';
import api from '../../services/api';
import { createPortal } from 'react-dom';
import { exportTimetableToPDF } from '../../utils/timetablePDF';

// ─── Constants ─────────────────────────────────────────────────────────────────
const DAY_NAMES_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const MBBS_YEAR_LABELS = { 1: '1st Year', 2: '2nd Year', 3: '3rd Year', 4: '4th Year' };

const STATUS_DOT   = { PENDING:'#b5882a', ONGOING:'#1d4ed8', COMPLETED:'#166534', CANCELLED:'#6b7280' };
const STATUS_BG    = { PENDING:'#fef3c7', ONGOING:'#dbeafe', COMPLETED:'#dcfce7', CANCELLED:'#f3f4f6' };
const STATUS_LABEL = { PENDING:'Pending',  ONGOING:'Ongoing',  COMPLETED:'Completed', CANCELLED:'Cancelled' };

// ─── Helpers ───────────────────────────────────────────────────────────────────
function toLocalISO(date) {
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
}

function getWeekDates(refISO) {
  const [y,m,d] = refISO.split('-').map(Number);
  const ref = new Date(y, m-1, d);
  const dow = ref.getDay();
  const diffToMon = dow === 0 ? -6 : 1 - dow;
  return Array.from({ length: 6 }, (_, i) => {
    const dt = new Date(ref);
    dt.setDate(ref.getDate() + diffToMon + i);
    return toLocalISO(dt);
  });
}

function fmtShortDate(iso) {
  const [y,m,d] = iso.split('-').map(Number);
  return new Date(y,m-1,d).toLocaleDateString('en-IN',{ day:'numeric', month:'short' });
}

function fmtLongDate(iso) {
  const [y,m,d] = iso.split('-').map(Number);
  return new Date(y,m-1,d).toLocaleDateString('en-IN',{ weekday:'long', day:'numeric', month:'long', year:'numeric' });
}

function fmtDate(iso) {
  return iso ? new Date(iso+'T00:00:00').toLocaleDateString('en-IN',{ day:'2-digit', month:'short', year:'numeric' }) : '—';
}

const NAV_BTN = {
  padding:'6px 12px', borderRadius:7, border:'1px solid #e2e8f0',
  background:'#fff', cursor:'pointer', fontSize:12, fontWeight:600,
  color:'#374151', fontFamily:'inherit',
};

// ─── Class Detail Modal ────────────────────────────────────────────────────────
function ClassDetailModal({ cls, role, onClose, onAssign, onStatusUpdate }) {
  const [attRecords, setAttRecords]   = useState([]);
  const [loadingAtt, setLoadingAtt]   = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => {
    if (!cls || !cls.id) return;
    let active = true;
    setLoadingAtt(true);
    api.get(`/auth/class-attendance/?class_id=${cls.id}`)
      .then(res => {
        if (active) setAttRecords(Array.isArray(res.data) ? res.data : (res.data?.results || []));
      })
      .catch(err => console.error('Failed to load class session attendance:', err))
      .finally(() => {
        if (active) setLoadingAtt(false);
      });
    return () => { active = false; };
  }, [cls ? cls.id : null]);

  if (!cls) return null;
  const dot = STATUS_DOT[cls.status] || '#6b7280';
  const cbg = STATUS_BG[cls.status]  || '#f3f4f6';

  const presentCount = attRecords.filter(r => r.status === 'PRESENT' || r.status === 'LATE').length;
  const absentCount  = attRecords.filter(r => r.status === 'ABSENT').length;
  const totalCount   = attRecords.length;
  const attPct       = totalCount > 0 ? Math.round((presentCount / totalCount) * 100) : 0;

  const filteredRoster = attRecords.filter(r => {
    if (statusFilter === 'PRESENT') return r.status === 'PRESENT' || r.status === 'LATE';
    if (statusFilter === 'ABSENT') return r.status === 'ABSENT';
    return true;
  });

  return createPortal(
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, width:'100vw', height:'100vh', background:'rgba(15,23,42,0.65)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:999999, padding:16 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:680, maxHeight:'88vh', overflowY:'auto', boxShadow:'0 24px 60px rgba(0,0,0,.3)', display:'flex', flexDirection:'column' }} onClick={e=>e.stopPropagation()}>
        
        {/* Header */}
        <div style={{ background:cbg, borderBottom:`3px solid ${dot}`, padding:'18px 20px', borderRadius:'16px 16px 0 0', flexShrink: 0 }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12 }}>
            <div style={{ flex:1 }}>
              <div style={{ display:'flex', gap:6, marginBottom:8, flexWrap:'wrap', alignItems: 'center' }}>
                <span style={{ padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700, background:'#fff', color:dot, border:`1px solid ${dot}` }}>{cls.department_code || cls.department_name}</span>
                <span style={{ padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700, background:dot, color:'#fff' }}>{STATUS_LABEL[cls.status]||cls.status}</span>
                <span style={{
                  padding: '2px 12px',
                  borderRadius: 20,
                  fontSize: 11,
                  fontWeight: 800,
                  background: (cls.class_type === 'Lab/Practical' || cls.class_type === 'Practical') ? '#7e22ce' : '#1d4ed8',
                  color: '#ffffff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4
                }}>
                  <span>Class Type:</span>
                  <span>{(cls.class_type === 'Lab/Practical' || cls.class_type === 'Practical') ? '🔬 Lab/Practical' : '📖 Theory'}</span>
                </span>
              </div>
              <div style={{ fontSize:16, fontWeight:800, color:'#0f172a', lineHeight:1.3 }}>{cls.title}</div>
              {cls.topic && (
                <div style={{ marginTop: 6, fontSize: 13, fontWeight: 600, color: '#1d4ed8', background: '#ffffff', border: '1px solid #bfdbfe', padding: '4px 10px', borderRadius: 8 }}>
                  📖 Topic: <span style={{ color: '#0f172a', fontWeight: 500 }}>{cls.topic}</span>
                </div>
              )}
            </div>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,.8)', border:'1px solid #e5e7eb', borderRadius:8, width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:18, color:'#6b7280' }}>×</button>
          </div>
        </div>

        {/* Scrollable Modal Body */}
        <div style={{ padding:'18px 20px', display:'flex', flexDirection:'column', gap:14, overflowY:'auto' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            {[
              ['📅 Date',       fmtDate(cls.start_date)],
              ['📖 Topic',        cls.topic || cls.topic_name || '—'],
              ['🏷 Class Type', (cls.class_type === 'Lab/Practical' || cls.class_type === 'Practical') ? '🔬 Lab/Practical' : '📖 Theory'],
              ['🏫 MBBS Year',  MBBS_YEAR_LABELS[cls.mbbs_year]||`Year ${cls.mbbs_year}`],
              ['🏢 Department', cls.department_name||cls.department_code||'—'],
              ['⏰ Time Slot',  cls.time_slot_name||'—'],
            ].map(([l,v]) => (
              <div key={l} style={{
                background: l.includes('Class Type') ? ((cls.class_type === 'Lab/Practical' || cls.class_type === 'Practical') ? '#f3e8ff' : '#eff6ff') : '#f8fafc',
                borderRadius: 8,
                padding: '10px 12px',
                border: l.includes('Class Type') ? `1.5px solid ${(cls.class_type === 'Lab/Practical' || cls.class_type === 'Practical') ? '#d8b4fe' : '#93c5fd'}` : 'none'
              }}>
                <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:3 }}>{l}</div>
                <div style={{ fontSize:13, fontWeight:700, color: l.includes('Class Type') ? ((cls.class_type === 'Lab/Practical' || cls.class_type === 'Practical') ? '#6b21a8' : '#1d4ed8') : '#0f172a' }}>{v}</div>
              </div>
            ))}
            {cls.time_slot_start && (
              <div style={{ background:'#f8fafc', borderRadius:8, padding:'10px 12px', gridColumn:'span 2' }}>
                <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:3 }}>🕐 Time</div>
                <div style={{ fontSize:13, fontWeight:600, color:'#0f172a' }}>{cls.time_slot_start?.slice(0,5)} – {cls.time_slot_end?.slice(0,5)}</div>
              </div>
            )}
          </div>

          <div style={{ background:cls.assigned_to_name?'#f0fdf4':'#fef2f2', borderRadius:10, padding:'12px 14px', border:`1px solid ${cls.assigned_to_name?'#86efac':'#fecaca'}` }}>
            <div style={{ fontSize:11, fontWeight:700, color:cls.assigned_to_name?'#166534':'#991b1b', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:4 }}>
              {cls.assigned_to_name ? '👤 Assigned Faculty' : '⚠ Not Assigned'}
            </div>
            <div style={{ fontSize:13, fontWeight:700, color:'#0f172a' }}>{cls.assigned_to_name||'No faculty assigned yet'}</div>
          </div>

          {cls.status_note && (
            <div style={{ background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:8, padding:'10px 12px', fontSize:13, color:'#0c4a6e' }}>💬 {cls.status_note}</div>
          )}

          {/* ── Class Attendance Section Inside Modal ── */}
          <div style={{ marginTop: 6, borderTop: '2px solid #e2e8f0', paddingTop: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>📊 Session Class Attendance Details</span>
              <span style={{ fontSize: 12, fontWeight: 800, padding: '3px 10px', borderRadius: 20, background: attPct >= 75 ? '#dcfce7' : '#fee2e2', color: attPct >= 75 ? '#166534' : '#991b1b' }}>
                Rate: {attPct}%
              </span>
            </div>

            {/* Attendance KPI Pills & Roster Filter */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>
                <span style={{ color: '#166534', fontWeight: 700 }}>✓ Present: {presentCount}</span> &nbsp;·&nbsp; 
                <span style={{ color: '#991b1b', fontWeight: 700 }}>✗ Absent: {absentCount}</span> &nbsp;·&nbsp; 
                <span>Total Enrolled: {totalCount}</span>
              </div>

              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setStatusFilter('ALL')} style={{ padding: '3px 9px', borderRadius: 10, fontSize: 11, fontWeight: 700, border: '1px solid #cbd5e1', background: statusFilter === 'ALL' ? '#1d4ed8' : '#fff', color: statusFilter === 'ALL' ? '#fff' : '#475569', cursor: 'pointer' }}>
                  All ({totalCount})
                </button>
                <button onClick={() => setStatusFilter('PRESENT')} style={{ padding: '3px 9px', borderRadius: 10, fontSize: 11, fontWeight: 700, border: '1px solid #86efac', background: statusFilter === 'PRESENT' ? '#22c55e' : '#fff', color: statusFilter === 'PRESENT' ? '#fff' : '#166534', cursor: 'pointer' }}>
                  ✓ Present ({presentCount})
                </button>
                <button onClick={() => setStatusFilter('ABSENT')} style={{ padding: '3px 9px', borderRadius: 10, fontSize: 11, fontWeight: 700, border: '1px solid #fecaca', background: statusFilter === 'ABSENT' ? '#ef4444' : '#fff', color: statusFilter === 'ABSENT' ? '#fff' : '#991b1b', cursor: 'pointer' }}>
                  ✗ Absent ({absentCount})
                </button>
              </div>
            </div>

            {/* Attendance Roster Table */}
            {loadingAtt ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#64748b', fontSize: 13 }}>Loading attendance roster…</div>
            ) : filteredRoster.length === 0 ? (
              <div style={{ padding: 18, textAlign: 'center', color: '#94a3b8', background: '#f8fafc', borderRadius: 8, border: '1px dashed #cbd5e1', fontSize: 12.5 }}>
                No student attendance records found for this filter.
              </div>
            ) : (
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', color: '#64748b', fontSize: 10.5, textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>
                      <th style={{ padding: '8px 12px' }}>Student Name</th>
                      <th style={{ padding: '8px 12px' }}>Student ID</th>
                      <th style={{ padding: '8px 12px' }}>Status</th>
                      <th style={{ padding: '8px 12px' }}>Notes / Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRoster.map((r, idx) => (
                      <tr key={r.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 600, color: '#0f172a' }}>{r.student_name}</td>
                        <td style={{ padding: '8px 12px', color: '#475569' }}>{r.student_id_num || r.student_id_number || r.student_id || '—'}</td>
                        <td style={{ padding: '8px 12px' }}>
                          <span style={{
                            padding: '2px 8px', borderRadius: 10, fontSize: 10.5, fontWeight: 700,
                            background: (r.status === 'PRESENT' || r.status === 'LATE') ? '#dcfce7' : '#fee2e2',
                            color: (r.status === 'PRESENT' || r.status === 'LATE') ? '#166534' : '#991b1b'
                          }}>
                            {r.status}
                          </span>
                        </td>
                        <td style={{ padding: '8px 12px', fontSize: 11, color: '#64748b' }}>{r.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Role-based actions */}
          {role==='HOD' && cls.status!=='COMPLETED' && cls.status!=='CANCELLED' && onAssign && (
            <button onClick={() => { onAssign(cls); onClose(); }}
              style={{ padding:'10px 20px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#1d4ed8,#4f46e5)', color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer', width:'100%', fontFamily:'inherit', marginTop: 4 }}>
              {cls.assigned_to_name ? '↻ Reassign Faculty' : '+ Assign Faculty'}
            </button>
          )}
          {role==='FACULTY' && (cls.status==='PENDING'||cls.status==='ONGOING') && onStatusUpdate && (
            <button onClick={() => { onStatusUpdate(cls); onClose(); }}
              style={{ padding:'10px 20px', borderRadius:9, border:'none', background:cls.status==='PENDING'?'linear-gradient(135deg,#1d4ed8,#2563eb)':'linear-gradient(135deg,#059669,#10b981)', color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer', width:'100%', fontFamily:'inherit', marginTop: 4 }}>
              {cls.status==='PENDING' ? '▶ Start Class' : '✓ Mark as Completed'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export function ClassTimetableView({ classes, role, onAssignClick, onStatusClick, timetableTitle = 'Class Timetable', institutionName = 'Medical College ERP' }) {
  const todayISO = toLocalISO(new Date());
  const [weekRef, setWeekRef] = useState(todayISO);
  const [selClass, setSelClass] = useState(null);
  const [exportingPDF, setExportingPDF] = useState(false);

  const handleExportPDF = () => {
  setExportingPDF(true);
  try {
    exportTimetableToPDF({
      classes,
      title: timetableTitle,
      institutionName,
      fromDate: weekDates[0],          // ← Mon of the visible week
      toDate:   weekDates[5],          // ← Sat of the visible week
      showFaculty: role !== 'STUDENT',
      showStatus: true,
    });
  } finally {
    setTimeout(() => setExportingPDF(false), 1000);
  }
};

  const weekDates = useMemo(() => getWeekDates(weekRef), [weekRef]);

  // Smart week adjustment when classes/filters update:
  // If current week has no matching classes, auto-navigate to the week of the first filtered class
  React.useEffect(() => {
    if (classes && classes.length > 0) {
      const currentWeekDates = getWeekDates(weekRef);
      const hasClassInCurrentWeek = classes.some(c => c.start_date && currentWeekDates.includes(c.start_date));
      if (!hasClassInCurrentWeek) {
        const firstWithDate = classes.find(c => c.start_date)?.start_date;
        if (firstWithDate) {
          setWeekRef(firstWithDate);
        }
      }
    }
  }, [classes]);

  const prevWeek = () => { const d=new Date(weekRef+'T00:00:00'); d.setDate(d.getDate()-7); setWeekRef(toLocalISO(d)); };
  const nextWeek = () => { const d=new Date(weekRef+'T00:00:00'); d.setDate(d.getDate()+7); setWeekRef(toLocalISO(d)); };

  const { timeSlots, grid } = useMemo(() => {
    const slotMap = {};
    classes.forEach(c => {
      const k = c.time_slot_name||'__none__';
      if (!slotMap[k]) slotMap[k] = { name:k, start:c.time_slot_start, end:c.time_slot_end };
    });
    const timeSlots = Object.values(slotMap).sort((a,b) => {
      if (a.name==='__none__') return 1;
      if (b.name==='__none__') return -1;
      return (a.start||'').localeCompare(b.start||'');
    });
    const grid = {};
    timeSlots.forEach(s => { grid[s.name] = {}; });
    classes.forEach(c => {
      const k = c.time_slot_name||'__none__';
      if (!grid[k]) grid[k] = {};
      if (!grid[k][c.start_date]) grid[k][c.start_date] = [];
      grid[k][c.start_date].push(c);
    });
    return { timeSlots, grid };
  }, [classes]);

  const weekClassCount = classes.filter(c => weekDates.includes(c.start_date)).length;

  if (classes.length === 0) return (
    <div style={{ textAlign:'center', padding:'50px 24px', color:'#94a3b8', background:'#f8fafc', borderRadius:12, border:'2px dashed #e2e8f0' }}>
      No classes to display in timetable view.
    </div>
  );

  return (
    <div style={{ fontFamily:"'DM Sans','Segoe UI',sans-serif", display:'flex', flexDirection:'column', gap:12 }}>
      {/* Week navigation */}
      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
        <button onClick={prevWeek} style={NAV_BTN}>‹ Prev Week</button>
        <button onClick={() => setWeekRef(todayISO)} style={{ ...NAV_BTN, background:'#1d4ed8', color:'#fff', border:'none' }}>Today</button>
        <button onClick={nextWeek} style={NAV_BTN}>Next Week ›</button>
        <span style={{ fontSize:13, fontWeight:700, color:'#334155', marginLeft:4 }}>
          {fmtShortDate(weekDates[0])} – {fmtShortDate(weekDates[5])}
        </span>
        <span style={{ fontSize:12, color:'#94a3b8' }}>({weekClassCount} classes this week)</span>
      </div>

      {/* Grid */}
      <div style={{ overflowX:'auto', borderRadius:12, border:'1px solid #e5e7eb' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', minWidth:700 }}>
          <thead>
            <tr style={{ background:'#f8fafc' }}>
              {weekDates.map((iso, di) => {
                const isToday = iso === todayISO;
                return (
                  <th key={di} style={{ padding:'10px 6px', textAlign:'center', fontSize:11, fontWeight:700, color:isToday?'#1d4ed8':'#64748b', borderBottom:`2px solid ${isToday?'#1d4ed8':'#e5e7eb'}`, borderRight:'1px solid #f1f5f9', background:isToday?'#eff6ff':'transparent' }}>
                    <div style={{ fontWeight:800 }}>{DAY_NAMES_SHORT[di]}</div>
                    <div style={{ fontSize:10, marginTop:2 }}>{fmtShortDate(iso)}</div>
                    {isToday && <div style={{ fontSize:9, color:'#1d4ed8', fontWeight:800, marginTop:1 }}>TODAY</div>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {timeSlots.map((slot, si) => (
              <tr key={slot.name} style={{ background:si%2===0?'#fff':'#fafafa' }}>
                {weekDates.map((iso, di) => {
                  const cellClasses = grid[slot.name]?.[iso] || [];
                  return (
                    <td key={di} style={{ padding:6, verticalAlign:'top', borderRight:'1px solid #f1f5f9', borderBottom:'1px solid #f1f5f9', minWidth:120 }}>
                      {cellClasses.map(c => {
                        const dot = STATUS_DOT[c.status]||'#6b7280';
                        const cbg = STATUS_BG[c.status]||'#f3f4f6';
                        return (
                          <div key={c.id} onClick={() => setSelClass(c)}
                            style={{ background:cbg, border:`1px solid ${dot}33`, borderLeft:`3px solid ${dot}`, borderRadius:7, padding:'6px 8px', marginBottom:4, cursor:'pointer', transition:'box-shadow .12s' }}
                            onMouseEnter={e => e.currentTarget.style.boxShadow=`0 0 0 2px ${dot}55`}
                            onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
                            <div style={{ fontWeight:700, color:'#0f172a', fontSize:11 }}>{c.department_code}</div>
                            <div style={{ fontSize:10, color:'#475569', lineHeight:1.3, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>{c.title}</div>
                            {c.assigned_to_name && <div style={{ fontSize:10, color:'#64748b', marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.assigned_to_name}</div>}
                            {!c.assigned_to_name && role==='HOD' && <div style={{ fontSize:10, color:'#ef4444', fontWeight:700, marginTop:2 }}>⚠ Unassigned</div>}
                            <div style={{ display:'flex', alignItems:'center', gap:4, marginTop:4 }}>
                              <span style={{ width:6, height:6, borderRadius:'50%', background:dot, flexShrink:0 }} />
                              <span style={{ fontSize:10, color:dot, fontWeight:600 }}>{STATUS_LABEL[c.status]}</span>
                            </div>
                          </div>
                        );
                      })}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
        {Object.entries(STATUS_LABEL).map(([k,v]) => (
          <span key={k} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#64748b' }}>
            <span style={{ width:8, height:8, borderRadius:2, background:STATUS_DOT[k], flexShrink:0 }} />{v}
          </span>
        ))}
        <span style={{ marginLeft:'auto', fontSize:11, color:'#94a3b8' }}>Click any class card for details & actions</span>
      </div>

      {selClass && (
        <ClassDetailModal cls={selClass} role={role} onClose={() => setSelClass(null)} onAssign={onAssignClick} onStatusUpdate={onStatusClick} />
      )}
    </div>
  );
}

// ─── ClassCalendarView ─────────────────────────────────────────────────────────
export function ClassCalendarView({ classes, role, onAssignClick, onStatusClick, timetableTitle = 'Class Timetable', institutionName = 'Medical College ERP' }) {
  const todayISO = toLocalISO(new Date());
  const [viewYear,  setViewYear]  = useState(new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const [selDate,   setSelDate]   = useState(todayISO);
  const [selClass,  setSelClass]  = useState(null);
  const [exportingPDF, setExportingPDF] = useState(false);

  const handleExportPDF = () => {
  setExportingPDF(true);
  // Export the entire visible month (Mon–Sat days only, handled inside utility)
  const firstDay = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-01`;
  const lastDay  = toLocalISO(new Date(viewYear, viewMonth + 1, 0)); // last day of month
  try {
    exportTimetableToPDF({
      classes,
      title: timetableTitle,
      institutionName,
      fromDate: firstDay,              // ← 1st of the visible month
      toDate:   lastDay,              // ← last day of the visible month
      showFaculty: role !== 'STUDENT',
      showStatus: true,
    });
  } finally {
    setTimeout(() => setExportingPDF(false), 1000);
  }
};

  const prevMonth = () => { if (viewMonth===0){setViewYear(y=>y-1);setViewMonth(11);}else setViewMonth(m=>m-1); };
  const nextMonth = () => { if (viewMonth===11){setViewYear(y=>y+1);setViewMonth(0);}else setViewMonth(m=>m+1); };

  // Smart month adjustment when classes/filters update:
  React.useEffect(() => {
    if (classes && classes.length > 0) {
      const selectedMonthPrefix = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;
      const hasClassInCurrentMonth = classes.some(c => c.start_date && c.start_date.startsWith(selectedMonthPrefix));
      if (!hasClassInCurrentMonth) {
        const firstWithDate = classes.find(c => c.start_date)?.start_date;
        if (firstWithDate) {
          const [y, m] = firstWithDate.split('-').map(Number);
          setViewYear(y);
          setViewMonth(m - 1);
          setSelDate(firstWithDate);
        }
      }
    }
  }, [classes]);

  const classCountByDate = useMemo(() => {
    const map = {};
    classes.forEach(c => { if (c.start_date) map[c.start_date]=(map[c.start_date]||0)+1; });
    return map;
  }, [classes]);

  const dayClasses = useMemo(() =>
    selDate ? classes.filter(c => c.start_date===selDate).sort((a,b)=>(a.time_slot_start||'').localeCompare(b.time_slot_start||'')) : [],
  [classes, selDate]);

  const daysInMonth = new Date(viewYear, viewMonth+1, 0).getDate();
  const firstDow    = (() => { const d=new Date(viewYear,viewMonth,1).getDay(); return (d+6)%7; })();
  const cells = [];
  for (let i=0; i<firstDow; i++) cells.push(null);
  for (let d=1; d<=daysInMonth; d++) cells.push(d);

  const monthClassCount = Object.entries(classCountByDate)
    .filter(([d]) => d.startsWith(`${viewYear}-${String(viewMonth+1).padStart(2,'0')}`))
    .reduce((sum,[,n])=>sum+n, 0);

  return (
    <div style={{ fontFamily:"'DM Sans','Segoe UI',sans-serif", display:'flex', gap:20, flexWrap:'wrap', alignItems:'flex-start' }}>

      {/* Calendar panel */}
      <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:14, padding:18, minWidth:286, flex:'0 0 auto' }}>
        {/* Navigation */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
          <button onClick={prevMonth} style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:'#64748b', padding:'2px 8px' }}>‹</button>
          <span style={{ fontSize:14, fontWeight:800, color:'#0f172a' }}>{MONTH_NAMES[viewMonth]} {viewYear}</span>
          <button onClick={nextMonth} style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:'#64748b', padding:'2px 8px' }}>›</button>
        </div>

        {/* Day-of-week headers */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2, marginBottom:6 }}>
          {['M','T','W','T','F','S','S'].map((d,i) => (
            <div key={i} style={{ textAlign:'center', fontSize:10, fontWeight:700, color:i===6?'#fca5a5':'#94a3b8', padding:'2px 0' }}>{d}</div>
          ))}
        </div>

        {/* Day cells */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3 }}>
          {cells.map((day, idx) => {
            if (!day) return <div key={idx} />;
            const dow = (firstDow+day-1)%7;
            const isSun = dow===6;
            const iso = `${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
            const isToday    = iso===todayISO;
            const isSelected = iso===selDate;
            const count      = classCountByDate[iso]||0;
            return (
              <div key={idx} onClick={() => { if (!isSun) setSelDate(iso); }}
                style={{
                  aspectRatio:'1', display:'flex', flexDirection:'column',
                  alignItems:'center', justifyContent:'center', borderRadius:8,
                  cursor:isSun?'default':'pointer', position:'relative',
                  background:isSelected?'#1d4ed8':isToday?'#eff6ff':'transparent',
                  color:isSelected?'#fff':isSun?'#fca5a5':'#0f172a',
                  border:isToday&&!isSelected?'1.5px solid #1d4ed8':'1.5px solid transparent',
                  opacity:isSun?0.35:1, fontSize:12,
                  fontWeight:(isToday||isSelected||count>0)?700:400,
                  transition:'background .1s',
                }}>
                <span style={{ lineHeight:1 }}>{day}</span>
                {count>0 && (
                  <div style={{ display:'flex', gap:2, position:'absolute', bottom:3, left:'50%', transform:'translateX(-50%)' }}>
                    {Array.from({length:Math.min(count,3)},(_,i)=>(
                      <div key={i} style={{ width:4, height:4, borderRadius:'50%', background:isSelected?'#fff':'#1d4ed8' }} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend + month count */}
        <div style={{ marginTop:14, display:'flex', gap:12, flexWrap:'wrap' }}>
          <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:10, color:'#64748b' }}>
            <span style={{ width:6, height:6, borderRadius:'50%', background:'#1d4ed8' }} /> Has classes
          </span>
          <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:10, color:'#64748b' }}>
            <span style={{ width:10, height:10, borderRadius:3, background:'#eff6ff', border:'1px solid #1d4ed8', display:'inline-block' }} /> Today
          </span>
        </div>
        <div style={{ marginTop:10, padding:'10px 12px', background:'#f8fafc', borderRadius:8, fontSize:12, color:'#64748b' }}>
          📊 {monthClassCount} classes in {MONTH_NAMES[viewMonth]}
        </div>
        <button
          onClick={handleExportPDF}
          disabled={exportingPDF}
          style={{ marginTop:10, width:'100%', padding:'8px 0', borderRadius:8, border:'1px solid #334155', background:'#fff', cursor:'pointer', fontSize:12, fontWeight:700, color:'#334155', fontFamily:'inherit', opacity: exportingPDF ? 0.6 : 1 }}
        >
          {exportingPDF ? '⏳ Preparing…' : '🖨 Export Week PDF'}
        </button>
      </div>

      {/* Day detail panel */}
      <div style={{ flex:1, minWidth:260, display:'flex', flexDirection:'column', gap:12 }}>
        <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12, padding:'14px 18px' }}>
          <div style={{ fontSize:14, fontWeight:800, color:'#0f172a' }}>
            {selDate ? fmtLongDate(selDate) : 'Select a date'}
          </div>
          {selDate && (
            <div style={{ fontSize:12, color:'#64748b', marginTop:4 }}>
              {dayClasses.length===0 ? 'No classes scheduled' : `${dayClasses.length} class${dayClasses.length!==1?'es':''} scheduled`}
            </div>
          )}
        </div>

        {!selDate ? (
          <div style={{ textAlign:'center', padding:'36px 20px', color:'#94a3b8', background:'#f8fafc', borderRadius:12, border:'2px dashed #e2e8f0', fontSize:13 }}>
            Click a date to see scheduled classes
          </div>
        ) : dayClasses.length===0 ? (
          <div style={{ textAlign:'center', padding:'36px 20px', color:'#94a3b8', background:'#f8fafc', borderRadius:12, border:'2px dashed #e2e8f0', fontSize:13 }}>
            No classes on this date
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {dayClasses.map(c => {
              const dot = STATUS_DOT[c.status]||'#6b7280';
              const cbg = STATUS_BG[c.status]||'#f3f4f6';
              return (
                <div key={c.id} onClick={() => setSelClass(c)}
                  style={{ background:'#fff', border:'1px solid #e5e7eb', borderLeft:`4px solid ${dot}`, borderRadius:10, padding:'14px 16px', cursor:'pointer', transition:'box-shadow .12s, transform .1s' }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,.1)'; e.currentTarget.style.transform='translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow='none'; e.currentTarget.style.transform='none'; }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:800, fontSize:14, color:'#0f172a', marginBottom:4 }}>{c.title}</div>
                      <div style={{ fontSize:12, color:'#64748b' }}>{c.department_name} · {MBBS_YEAR_LABELS[c.mbbs_year]}</div>
                      {c.time_slot_name && <div style={{ fontSize:12, color:'#1d4ed8', marginTop:3 }}>⏰ {c.time_slot_name}{c.time_slot_start&&` · ${c.time_slot_start.slice(0,5)}–${c.time_slot_end?.slice(0,5)}`}</div>}
                    </div>
                    <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, background:cbg, color:dot, whiteSpace:'nowrap' }}>{STATUS_LABEL[c.status]}</span>
                  </div>
                  <div style={{ marginTop:10, display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                    <div style={{ fontSize:12, color:c.assigned_to_name?'#166534':'#ef4444', fontWeight:600 }}>
                      {c.assigned_to_name ? `👤 ${c.assigned_to_name}` : '⚠ Unassigned'}
                    </div>
                    <div style={{ display:'flex', gap:6 }}>
                      {role==='HOD' && c.status!=='COMPLETED' && c.status!=='CANCELLED' && onAssignClick && (
                        <button onClick={e => { e.stopPropagation(); onAssignClick(c); }}
                          style={{ padding:'4px 12px', borderRadius:6, border:'none', background:'#1d4ed8', color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
                          {c.assigned_to_name ? '↻ Reassign' : '+ Assign'}
                        </button>
                      )}
                      {role==='FACULTY' && (c.status==='PENDING'||c.status==='ONGOING') && onStatusClick && (
                        <button onClick={e => { e.stopPropagation(); onStatusClick(c); }}
                          style={{ padding:'4px 12px', borderRadius:6, border:'none', background:c.status==='PENDING'?'#1d4ed8':'#059669', color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
                          {c.status==='PENDING' ? '▶ Start' : '✓ Complete'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selClass && (
        <ClassDetailModal cls={selClass} role={role} onClose={() => setSelClass(null)} onAssign={onAssignClick} onStatusUpdate={onStatusClick} />
      )}
    </div>
  );
}